/**
 * endpointVisibility — hiding a table/endpoint from the app without deleting it.
 *
 * The flag lives in the org config's `api_endpoint_overrides`, keyed by endpoint
 * slug, alongside the `name`/`description` overrides that map already carries:
 *
 * ```jsonc
 * "api_endpoint_overrides": {
 *   "org999_place":        { "name": "Culture Sites" },
 *   "org999_polygontest":  { "hidden": true }
 * }
 * ```
 *
 * **Writes must merge, never replace.** The backend stores this as one JSON
 * column, so PATCHing a map built from the hidden flags alone would drop every
 * `name`/`description` override in it — renaming every table in the org as a side
 * effect of hiding one. {@link withEndpointHidden} is the only supported way to
 * build the next map: it copies what is there and edits a single entry.
 *
 * Reading is deliberately tolerant: the map is hand-authored in some orgs'
 * `config.json`, so an entry may be missing, null, or not an object.
 */

/** One endpoint's overrides. `name`/`description` predate this module. */
export interface EndpointOverride {
  name?: string;
  description?: string;
  hidden?: boolean;
  [key: string]: any;
}

export type EndpointOverrides = Record<string, EndpointOverride>;

/** Narrows one entry of the overrides map, tolerating junk. */
const entryFor = (overrides: EndpointOverrides | undefined | null, slug: string): EndpointOverride | undefined => {
  const entry = overrides?.[slug];
  return entry && typeof entry === "object" && !Array.isArray(entry) ? entry : undefined;
};

/** True when this endpoint is hidden from the app. Only an explicit `true` counts. */
export function isEndpointHidden(overrides: EndpointOverrides | undefined | null, slug: string | undefined | null): boolean {
  if (!slug) return false;
  return entryFor(overrides, slug)?.hidden === true;
}

/** Every slug currently hidden, for a one-shot filter. */
export function hiddenEndpointSlugs(overrides: EndpointOverrides | undefined | null): string[] {
  if (!overrides || typeof overrides !== "object") return [];
  return Object.keys(overrides).filter((slug) => isEndpointHidden(overrides, slug));
}

/**
 * The overrides map with `slug` hidden or shown, everything else untouched.
 *
 * Un-hiding **removes** the `hidden` key rather than writing `hidden: false`, and
 * drops the entry entirely once nothing else is left in it, so the stored map does
 * not accumulate `{ "slug": { "hidden": false } }` noise for every table anyone
 * ever toggled.
 */
export function withEndpointHidden(overrides: EndpointOverrides | undefined | null, slug: string, hidden: boolean): EndpointOverrides {
  const base: EndpointOverrides = overrides && typeof overrides === "object" ? { ...overrides } : {};
  if (!slug) return base;

  if (hidden) {
    base[slug] = { ...entryFor(base, slug), hidden: true };
    return base;
  }

  const { hidden: _dropped, ...rest } = entryFor(base, slug) ?? {};
  if (Object.keys(rest).length) base[slug] = rest;
  else delete base[slug];
  return base;
}
